import { createContext } from 'react';
import Keycloak from 'keycloak-js';
import { keycloakAuthUrl } from 'src/config';
import { redirect } from 'react-router';
import { paths } from 'src/paths';
import { getRealmName } from 'src/utils/get-realm-name';
import { recordLoginContext } from 'src/services/clientIpService/recordLoginContext';

declare global {
  interface Window {
    Intercom?: (event: string, options?: { [key: string]: any }) => void;
  }
}

const hostName = window.location.hostname;
// Path-based auth checks in this module read this — e.g. the guard that skips Keycloak init on the
// public invoice-download page. Kept declared even while nothing in this file's current contents
// references it: dropping it merges cleanly against a branch that does use it, and the break then
// surfaces only as a build error and a module-load ReferenceError.
const pathName = window.location.pathname;

// Re-exported so existing `import { getRealmName } from 'src/routes/AuthContext'` call sites keep
// working; the implementation now lives in `src/utils/get-realm-name`.
export { getRealmName };

export const keycloak = new Keycloak({
  url: keycloakAuthUrl,
  realm: getRealmName() || '',
  clientId: 'js-client',
});

// BOPS-182: the public invoice-download page is auth-free (rendered before the auth gate in
// app.tsx). Skip Keycloak init on that path — otherwise check-sso runs on every load and, on
// mobile Safari (3rd-party cookies blocked), its redirect-based fallback bounces the recipient to
// the login screen and drops the ?t= token.
//
// APIT-696: the self-service "forgot password" page (also rendered before the auth gate — see
// app.tsx) needs the same exemption. check-sso's silent-check iframe falls back to a full-page
// redirect when it can't be used (e.g. mobile Safari blocking 3rd-party cookies — the case
// BOPS-182 hit), and that redirect returns to the site root, not this path, so the app would then
// call `auth.login()` and bounce a signed-out visitor to the Keycloak login instead of rendering
// the page. `endsWith` also covers the localhost `/{realm}/…` form, matching app.tsx.
if (!pathName.endsWith(paths.invoiceDownload) && !pathName.endsWith(paths.auth.forgotPassword)) {
  try {
    //  @ts-ignore
    await keycloak.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
      redirectUri:
        hostName === 'localhost'
          ? `${window.location.origin}/${getRealmName()}`
          : `${window.location.origin}`,
    });
    // RA-2160: deliberately not awaited, so sign-in and first render never wait on it or on ipify.
    // Once per Keycloak session (sid guard); silent refreshes keep the sid and don't re-fire.
    void recordLoginContext(keycloak);
  } catch (error) {
    console.error('Failed to initialize keycloak adapter:', error);
  }
}

const AuthContext = createContext<Keycloak>(keycloak);

export interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return <AuthContext.Provider value={keycloak}>{children}</AuthContext.Provider>;
};

export const TOKEN_REFRESHED_EVENT = 'keycloak-token-refreshed';

export const logoutUser = () => {
  // Shutdown Intercom session before logout
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('shutdown');
  }

  const realm = localStorage.getItem('realm') || '';
  localStorage.clear();
  if (hostName === 'localhost') {
    keycloak.logout({ redirectUri: `${window.location.origin}/${realm}` });
  } else {
    keycloak.logout({ redirectUri: window.location.origin });
  }
};

export const getAccessToken = (minValidity = 30) => {
  return new Promise<string>((resolve, reject) => {
    if (keycloak.authenticated) {
      keycloak
        .updateToken(minValidity)
        .then(() => {
          window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT));
          resolve(keycloak.token as string);
        })
        .catch(() => {
          reject('Failed to get access token');
        });
    } else {
      const realm = localStorage.getItem('realm') || '';
      localStorage.clear();

      if (hostName === 'localhost') {
        window.location.assign(`${window.location.origin}/${realm}`);
      } else {
        window.location.assign(window.location.origin);
      }
    }
  });
};

export default AuthContext;
